import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Link } from "react-router-dom";
import { 
  Shield, 
  Award, 
  Clock, 
  Users, 
  Phone,
  Mail,
  MapPin,
  Settings,
  Hammer,
  Building,
  HardHat,
  Wrench,
  Zap,
  Truck,
  Cog,
  CheckCircle
} from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import SchemaMarkup from "@/components/SchemaMarkup";
import SEOMeta from "@/components/SEOMeta";

// Import images
import safetyOfficer from "@/assets/safety-officer.jpg";
import designEngineering from "@/assets/design-engineering.jpg";
import steelErector from "@/assets/steel-erector.jpg";
import skilledTradesTeam from "@/assets/skilled-trades-team.jpg";
import specialtySafetyOfficer from "@/assets/specialty-safety-officer.jpg";
import specialtyDesignEngineering from "@/assets/specialty-design-engineering.jpg";
import specialtySteelErection from "@/assets/specialty-steel-erection.jpg";
import specialtyTradesTeam from "@/assets/specialty-trades-team.jpg";

const SpecialtyContractingServices = () => {
  const faqData = [
    {
      question: "What types of specialty contracting services do you provide?",
      answer: "We provide specialized construction services including safety consulting, design engineering, steel erection, project management, quality control, and technical consulting for complex industrial and commercial projects."
    },
    {
      question: "Do you have certified specialists for specialty contracting work?",
      answer: "Yes, our team includes certified safety officers, professional engineers, licensed steel erectors, and specialized technicians with extensive experience in their respective fields."
    },
    {
      question: "Can you handle complex industrial and commercial specialty projects?",
      answer: "Absolutely. We specialize in complex projects requiring specialized knowledge, including high-rise construction, industrial facilities, infrastructure projects, and custom engineering solutions."
    },
    {
      question: "What areas in Ontario do you serve for specialty contracting?",
      answer: "We provide specialty contracting services throughout Ontario, including Toronto, Ottawa, Hamilton, London, Windsor, and all major industrial centers across the province."
    },
    {
      question: "Do you offer project management for specialty contracting projects?",
      answer: "Yes, we provide comprehensive project management services, coordinating multiple specialty trades and ensuring projects meet all technical specifications, safety standards, and deadlines."
    },
    {
      question: "What safety standards do you follow for specialty contracting work?",
      answer: "We exceed Ontario health and safety regulations, maintain specialized safety certifications, conduct regular safety training, and implement project-specific safety protocols for all specialty work."
    }
  ];

  const serviceData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Specialty Contracting Services Ontario",
    "description": "Professional specialty contracting services including safety consulting, design engineering, steel erection, and technical project management across Ontario.",
    "provider": {
      "@type": "LocalBusiness",
      "name": "Blue Knight Contracting",
      "address": {
        "@type": "PostalAddress",
        "addressLocality": "Ontario",
        "addressCountry": "CA"
      },
      "telephone": "+1-647-760-8854"
    },
    "areaServed": "Ontario, Canada",
    "hasOfferCatalog": {
      "@type": "OfferCatalog",
      "name": "Specialty Contracting Services",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Safety Consulting",
            "description": "Professional safety consulting and compliance services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Design Engineering",
            "description": "Technical design and engineering services"
          }
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": "Steel Erection",
            "description": "Professional steel erection and structural work"
          }
        }
      ]
    }
  };

  const specialtyServices = [
    {
      title: "Safety Consulting & Compliance",
      description: "Professional safety officers and consultants ensuring full compliance with Ontario safety regulations.",
      icon: Shield,
      services: [
        "Safety Program Development",
        "Site Safety Inspections",
        "Safety Training & Certification",
        "Compliance Auditing",
        "Risk Assessment",
        "Emergency Response Planning"
      ]
    },
    {
      title: "Design Engineering",
      description: "Technical design and engineering services for complex construction and infrastructure projects.",
      icon: Settings,
      services: [
        "Structural Engineering",
        "CAD Design Services", 
        "Technical Drawings",
        "Project Specifications",
        "Code Compliance Review",
        "Value Engineering"
      ]
    },
    {
      title: "Steel Erection & Structural",
      description: "Specialized steel erection services for high-rise buildings and industrial structures.",
      icon: Building,
      services: [
        "High-Rise Steel Erection",
        "Industrial Structural Work",
        "Bridge Construction",
        "Crane Services",
        "Rigging & Lifting",
        "Structural Welding"
      ]
    },
    {
      title: "Project Management",
      description: "Comprehensive project management for complex specialty contracting projects.",
      icon: CheckCircle,
      services: [
        "Project Planning & Scheduling",
        "Resource Coordination",
        "Quality Control",
        "Progress Monitoring",
        "Budget Management",
        "Client Communication"
      ]
    },
    {
      title: "Technical Consulting",
      description: "Expert technical consulting for specialized construction challenges and solutions.",
      icon: Cog,
      services: [
        "Construction Consulting",
        "Technical Problem Solving",
        "Method Development",
        "Equipment Selection",
        "Process Optimization",
        "Performance Analysis"
      ]
    },
    {
      title: "Quality Assurance",
      description: "Comprehensive quality assurance and control services for specialty construction projects.",
      icon: Award,
      services: [
        "Quality Control Plans",
        "Material Testing",
        "Progress Inspections",
        "Documentation Management",
        "Compliance Verification",
        "Final Quality Reviews"
      ]
    }
  ];

  const serviceAreas = [
    "Toronto", "Ottawa", "Hamilton", "London", "Windsor", "Kitchener-Waterloo",
    "Thunder Bay", "Sudbury", "Kingston", "Barrie", "Oshawa", "St. Catharines",
    "Cambridge", "Guelph", "Brantford", "Peterborough", "Sarnia", "Belleville"
  ];

  return (
    <>
      <SEOMeta 
        title="Specialty Contracting Ontario | Expert Construction Services | Niagara Region, Hamilton and Toronto GTA"
        description="Professional specialty contracting services in Ontario. Safety consulting, design engineering, steel erection & project management. Serving Toronto, Hamilton, Niagara & GTA."
        keywords="specialty contracting Ontario, safety consulting Toronto, design engineering Hamilton, steel erection Niagara, project management GTA, construction consulting, Niagara Region specialty contracting, Hamilton specialty contracting, Toronto GTA specialty contracting"
        canonicalUrl="https://blueknightcontracting.ca/services/specialty-contracting"
      />
      <SchemaMarkup type="Service" data={serviceData} />
      <SchemaMarkup type="FAQ" data={faqData} />
      
      <Header />
      
      <main className="min-h-screen">
        {/* Hero Section */}
        <section className="relative bg-gradient-to-br from-primary via-primary-dark to-secondary py-24 lg:py-32 overflow-hidden">
          <div className="absolute inset-0 bg-black/20"></div>
          <div className="container mx-auto px-4 relative z-10">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div className="text-white">
                <Badge variant="secondary" className="mb-4 bg-white/10 text-white border-white/20">
                  Professional Specialty Contracting
                </Badge>
                <h1 className="text-4xl lg:text-6xl font-bold mb-6 leading-tight">
                  Specialty Contracting Services Across Ontario
                </h1>
                <p className="text-xl lg:text-2xl mb-8 text-white/90 leading-relaxed">
                  Expert specialty contracting services including safety consulting, design engineering, steel erection, and technical project management. Certified specialists and proven expertise for complex projects.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                    <Link to="/contact">Get Expert Consultation</Link>
                  </Button>
                  <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                    <a href="tel:+16477608854">Call: (647) 760-8854</a>
                  </Button>
                </div>
                <div className="flex flex-wrap gap-6 text-white/80">
                  <div className="flex items-center gap-2">
                    <Shield className="h-5 w-5" />
                    <span>Certified Specialists</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Award className="h-5 w-5" />
                    <span>Professional Engineers</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Clock className="h-5 w-5" />
                    <span>Project Management</span>
                  </div>
                </div>
              </div>
              <div className="relative">
                <img 
                  src={safetyOfficer} 
                  alt="Professional specialty contracting services in Ontario" 
                  className="w-full h-auto rounded-lg shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Services Overview */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Our Specialty Services</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Comprehensive Specialty Contracting Solutions
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                From safety consulting to complex engineering projects, we provide specialized expertise for the most demanding construction challenges.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {specialtyServices.map((service, index) => (
                <Card key={index} className="h-full hover:shadow-lg transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <div className="flex items-center gap-4 mb-4">
                      <div className="p-3 rounded-full bg-primary/10">
                        <service.icon className="h-8 w-8 text-primary" />
                      </div>
                      <CardTitle className="text-xl">{service.title}</CardTitle>
                    </div>
                    <CardDescription className="text-base">
                      {service.description}
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <ul className="space-y-2">
                      {service.services.map((item, idx) => (
                        <li key={idx} className="flex items-center gap-2 text-sm">
                          <div className="w-1.5 h-1.5 rounded-full bg-primary flex-shrink-0"></div>
                          {item}
                        </li>
                      ))}
                    </ul>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Featured Projects */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Specialty Projects</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Complex Specialty Contracting Projects
              </h2>
              <p className="text-xl text-muted-foreground">
                See our specialized expertise in action across various complex construction and engineering projects.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={specialtySafetyOfficer} 
                    alt="Professional safety consulting services" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Safety Consulting</h3>
                    <p className="text-sm text-white/80">Professional compliance</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={specialtyDesignEngineering} 
                    alt="Design engineering and technical services" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Design Engineering</h3>
                    <p className="text-sm text-white/80">Technical expertise</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={specialtySteelErection} 
                    alt="Professional steel erection services" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Steel Erection</h3>
                    <p className="text-sm text-white/80">Structural specialists</p>
                  </div>
                </div>
              </Card>
              
              <Card className="group overflow-hidden border-0 shadow-lg">
                <div className="relative overflow-hidden">
                  <img 
                    src={specialtyTradesTeam} 
                    alt="Specialty trades team coordination" 
                    className="w-full h-48 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent"></div>
                  <div className="absolute bottom-4 left-4 text-white">
                    <h3 className="font-semibold">Project Management</h3>
                    <p className="text-sm text-white/80">Expert coordination</p>
                  </div>
                </div>
              </Card>
            </div>
          </div>
        </section>

        {/* Technical Expertise CTA */}
        <section className="py-16 bg-primary text-primary-foreground">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <div className="flex items-center justify-center gap-3 mb-4">
                <Cog className="h-8 w-8" />
                <h2 className="text-3xl font-bold">Complex Project? We Have the Expertise</h2>
              </div>
              <p className="text-xl mb-8 opacity-90">
                Need specialized technical consulting or complex project management? Our certified specialists are ready to tackle your most challenging projects.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Request Technical Consultation</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <a href="tel:+16477608854">Call Specialists: (647) 760-8854</a>
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Why Choose Us */}
        <section className="py-20 bg-gradient-to-b from-secondary/5 to-background">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Why Choose Blue Knight Contracting</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Ontario's Premier Specialty Contracting Specialists
              </h2>
              <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                Decades of specialized experience, certified professionals, and commitment to excellence make us the trusted choice for complex projects.
              </p>
            </div>
            
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Award className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Certified Specialists</h3>
                <p className="text-muted-foreground">Professional engineers, safety officers, and certified specialists in every field.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Settings className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Technical Expertise</h3>
                <p className="text-muted-foreground">Advanced technical knowledge and specialized equipment for complex challenges.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <CheckCircle className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Project Management</h3>
                <p className="text-muted-foreground">Comprehensive project coordination ensuring on-time, on-budget delivery.</p>
              </Card>
              
              <Card className="text-center border-0 bg-white/60 backdrop-blur-sm p-6">
                <div className="p-4 rounded-full bg-primary/10 w-fit mx-auto mb-4">
                  <Shield className="h-8 w-8 text-primary" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Safety Excellence</h3>
                <p className="text-muted-foreground">Industry-leading safety standards and compliance with all regulations.</p>
              </Card>
            </div>
          </div>
        </section>

        {/* Service Areas */}
        <section className="py-20">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Service Coverage</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Specialty Contracting Services Across Ontario
              </h2>
              <p className="text-xl text-muted-foreground">
                We provide specialized contracting services throughout Ontario's major industrial and commercial centers.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto">
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
                {serviceAreas.map((area, index) => (
                  <Card key={index} className="text-center p-4 hover:shadow-md transition-shadow border-0 bg-white/60 backdrop-blur-sm">
                    <div className="flex items-center justify-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      <span className="font-medium">{area}</span>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-20 bg-gradient-to-b from-background to-secondary/5">
          <div className="container mx-auto px-4">
            <div className="text-center mb-16">
              <Badge variant="outline" className="mb-4">Frequently Asked Questions</Badge>
              <h2 className="text-3xl lg:text-4xl font-bold mb-4">
                Specialty Contracting Services FAQ
              </h2>
              <p className="text-xl text-muted-foreground">
                Common questions about our specialized contracting, consulting, and technical services.
              </p>
            </div>
            
            <div className="max-w-4xl mx-auto grid gap-6">
              {faqData.map((faq, index) => (
                <Card key={index} className="border-0 bg-white/60 backdrop-blur-sm">
                  <CardHeader>
                    <CardTitle className="text-left text-lg">{faq.question}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* Final CTA */}
        <section className="py-20 bg-gradient-to-br from-primary via-primary-dark to-secondary text-white">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl lg:text-4xl font-bold mb-6">
                Ready for Expert Specialty Contracting?
              </h2>
              <p className="text-xl mb-8 text-white/90">
                Get professional specialty contracting services with certified specialists and proven expertise. Contact us for a comprehensive project consultation.
              </p>
              
              <div className="grid md:grid-cols-3 gap-8 mb-12">
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Phone className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Call Us</h3>
                  <p className="text-white/80">
                    <a href="tel:+16477608854" className="hover:text-white transition-colors">
                      (647) 760-8854
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <Mail className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Email Us</h3>
                  <p className="text-white/80">
                    <a href="mailto:info@blueknightcontracting.ca" className="hover:text-white transition-colors">
                      info@blueknightcontracting.ca
                    </a>
                  </p>
                </div>
                
                <div className="text-center">
                  <div className="p-4 rounded-full bg-white/10 w-fit mx-auto mb-4">
                    <MapPin className="h-8 w-8" />
                  </div>
                  <h3 className="text-xl font-semibold mb-2">Service Area</h3>
                  <p className="text-white/80">All of Ontario</p>
                </div>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button size="lg" variant="secondary" className="text-lg px-8" asChild>
                  <Link to="/contact">Get Specialty Consultation</Link>
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-8 bg-white/10 border-white/30 text-white hover:bg-white/20" asChild>
                  <Link to="/gallery">View Our Projects</Link>
                </Button>
              </div>
            </div>
          </div>
        </section>
      </main>
      
      <Footer />
    </>
  );
};

export default SpecialtyContractingServices;